import { CheckOutlined, CloseOutlined, EyeOutlined, MoreOutlined } from "@ant-design/icons";
import { Button, ConfigProvider, Dropdown, Flex, Row, Space } from "antd";
import TableAntd from "components/elements/Table/Table";
import StatusTag from "components/elements/TagCustom/StatusTag";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";

function formatDate({ dateString, pattern = "YYYY-MM-DD", reFormat = "DD-MM-YYYY" }) {
  if (!dateString) return;
  const date = dayjs(dateString, pattern);
  return date.format(reFormat);
}

const PendingApprovalWarningLetterTable = ({
  isLoading = false,
  pagination = {},
  dataSource = [],
  pageType = "",
  onView = () => { },
  onChange = () => { },
  onApprove = () => { },
  onReject = () => { },
  onApproveAll = () => { },
  onRejectAll = () => { },
  getWarningLetterRequest = []
}) => {
  const { t, i18n } = useTranslation();
  let language = i18n.language === "en" ? "En" : "";

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const hasSelected = selectedRowKeys.length > 0;

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const columns = [
    {
      title: <div className="text-table">{t('req_id.req_id')}</div>,
      dataIndex: "sequence",
      key: "sequence",
    },
    {
      title: <div className="text-table">{t('name-surname')}</div>,
      dataIndex: "warnedEmployee",
      key: "warnedEmployee",
      render: (text, record) => {
        return i18n.language === 'en' ? <span>{record.warnedEmployee.nameAndSurnameEn}</span> : <span>{record.warnedEmployee.nameAndSurname}</span>
      }
    },
    {
      title: <div className="text-table">{t('department')}</div>,
      dataIndex: "departmentId",
      key: "departmentId",
      render: (text, record) => {
        return i18n.language === 'en' ? <span>{record.departmentId?.departmentNameEn}</span> : <span>{record.departmentId?.departmentName}</span>
      }
    },
    {
      title: <div className="text-table">{t('warning_letter.label.company-regulations')}</div>,
      dataIndex: "companyRegulations",
      key: "companyRegulations",
      render: (text, record) => {
        return i18n.language === 'en' ? <span>{record.companyRegulations?.companyRegulationEn}</span> : <span>{record.companyRegulations?.companyRegulation}</span>
      }
    },
    {
      title: <div className="text-table">{t('warning_letter.label.date_of_incident')}</div>,
      dataIndex: "incidentDate",
      key: "incidentDate",
      render: (_, record) => {
        return (
          <div>
            {formatDate({
              dateString: record?.incidentDate,
              reFormat: i18n.language === "en" ? "DD/MM/YYYY" : "DD/MM/BBBB",
            })}
          </div>
        );
      },
    },
    {
      title: <div className="text-table">{t('status.status')}</div>,
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (_, record) => {
        return <StatusTag value={record?.["approveStatus" + language]} />;
      },
    },
    {
      title: "",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <Dropdown
            menu={{
              items: [
                {
                  key: "1",
                  onClick: () => onView(record),
                  label: `${t("action.view")}`,
                  icon: (
                    <EyeOutlined style={{ fontSize: "18px", color: "gray" }} />
                  ),
                },
                {
                  key: "2",
                  onClick: () => onApprove(record),
                  label: `${t("action.approve")}`,
                  icon: (
                    <CheckOutlined style={{ fontSize: "18px", color: "gray" }} />
                  ),
                },
                {
                  key: "3",
                  onClick: () => onReject(record),
                  label: `${t("action.reject")}`,
                  icon: (
                    <CloseOutlined style={{ fontSize: "18px", color: "gray" }} />
                  ),
                },
              ],
            }}
          >
            <MoreOutlined style={{ fontSize: "25px", color: "gray" }} />
          </Dropdown>
        </Space>
      ),
    },

  ];

  return (
    <Flex vertical gap={16}>
      <ConfigProvider
        theme={{
          components: {
            Table: {
              selectionColumnWidth: "45px"
            },
          },
        }}
      >
        <TableAntd
          loading={isLoading}
          pagination={{
            ...pagination,
            showTotal: (total, range) =>
              t("paginate.description")
                .replace("{min}", range[0])
                .replace("{max}", range[1])
                .replace("{total}", total),
          }}
          dataSource={getWarningLetterRequest}
          rowKey={(record) => record.id}
          columns={columns}
          onChange={onChange}
          rowSelection={pageType === "view" ? rowSelection : undefined}
        />
      </ConfigProvider>

      {hasSelected && pageType === "view" && (
        <Flex vertical gap={8} style={{ alignItems: "end" }}>
          <span>{`${t("common.selected")} ${selectedRowKeys.length} ${t("common.items")}`}</span>
          <Row style={{ gap: "8px" }}>
            <Button
              type="default"
              style={{ borderRadius: "8px", height: "40px" }}
              onClick={() => {
                const selectedRecords = getWarningLetterRequest.filter((item) =>
                  selectedRowKeys.includes(item?.id)
                );
                onRejectAll(selectedRecords).then(() => {
                  setSelectedRowKeys([]);
                });
              }}
            >
              {t("request_approval.label.not_approved")}
            </Button>
            <Button
              type="primary"
              style={{ borderRadius: "8px", height: "40px", backgroundColor: "var(--button-color)" }}
              onClick={() => {
                const selectedRecords = getWarningLetterRequest.filter((item) =>
                  selectedRowKeys.includes(item?.id)
                );
                onApproveAll(selectedRecords).then(() => {
                  setSelectedRowKeys([]);
                });
              }}
            >
              {t("request_approval.label.approve")}
            </Button>
          </Row>
        </Flex>
      )}
    </Flex>
  );
};

export default PendingApprovalWarningLetterTable;